import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, GripVertical, X, ClipboardList, ChevronUp, ChevronDown, Eye, Copy, Star, Clock } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Btn, ConfirmModal, inputCls } from '../ui';
import Modal from '../ui/Modal';
import { useToast } from '../../contexts/NotificationContext';
import {
  getEmployeeInterviewTemplates, createEmployeeInterviewTemplate,
  updateEmployeeInterviewTemplate, deleteEmployeeInterviewTemplate,
} from '../../api';

const QUESTION_TYPES = [
  { value: 'section',  label: '── Section (titre)' },
  { value: 'text',     label: 'Texte court' },
  { value: 'textarea', label: 'Texte long' },
  { value: 'rating',   label: 'Note (1-5)' },
  { value: 'radio',    label: 'Choix unique' },
  { value: 'checkbox', label: 'Cases à cocher' },
];

const emptyQuestion = () => ({
  id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  label: '', type: 'text', options: [], required: false, description: '', duration: '',
});

const emptyForm = () => ({ name: '', questions: [emptyQuestion()], isDefault: false });

// ─── Question sortable ────────────────────────────────────────────────────────
const SortableQuestion = ({ q, idx, total, onUpdate, onRemove, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: q.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className={`bg-gray-50 rounded-lg p-3 ${isDragging ? 'ring-2 ring-primary' : ''}`}>
      <div className="flex items-start gap-2">
        <div className="flex flex-col items-center mt-1 w-6 gap-0.5">
          <button type="button" {...attributes} {...listeners} className="cursor-grab text-gray-300 hover:text-gray-500 active:cursor-grabbing">
            <GripVertical size={14} />
          </button>
          <button type="button" onClick={() => onMove(idx, -1)} disabled={idx === 0}
            className={`p-0.5 rounded ${idx === 0 ? 'text-gray-200' : 'text-gray-400 hover:text-primary'}`}>
            <ChevronUp size={10} />
          </button>
          <span className="text-[9px] text-gray-400 font-mono">{idx + 1}</span>
          <button type="button" onClick={() => onMove(idx, 1)} disabled={idx === total - 1}
            className={`p-0.5 rounded ${idx === total - 1 ? 'text-gray-200' : 'text-gray-400 hover:text-primary'}`}>
            <ChevronDown size={10} />
          </button>
        </div>

        <div className="flex-1 space-y-2">
          <input type="text" value={q.label} onChange={e => onUpdate(idx, 'label', e.target.value)}
            className={`w-full border rounded px-2 py-1.5 text-sm ${q.type === 'section' ? 'font-bold bg-primary/5 border-primary/20' : ''}`}
            placeholder={q.type === 'section' ? 'Titre de la section...' : 'Question...'} />

          {(q.type === 'section' || q.description) && (
            <textarea value={q.description || ''} onChange={e => onUpdate(idx, 'description', e.target.value)}
              className="w-full border rounded px-2 py-1 text-xs text-gray-500 resize-none" rows={2}
              placeholder={q.type === 'section' ? 'Instructions pour l\'intervieweur...' : 'Précisions sur la question...'} />
          )}
          {q.type !== 'section' && !q.description && (
            <button type="button" onClick={() => onUpdate(idx, 'description', ' ')}
              className="text-[10px] text-gray-400 hover:text-primary">+ Ajouter une description</button>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <select value={q.type} onChange={e => onUpdate(idx, 'type', e.target.value)} className="border rounded px-2 py-1 text-xs">
              {QUESTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            {q.type !== 'section' && (
              <label className="flex items-center gap-1 text-xs text-gray-500">
                <input type="checkbox" checked={q.required} onChange={e => onUpdate(idx, 'required', e.target.checked)} />
                Obligatoire
              </label>
            )}
            {q.type === 'section' && (
              <input type="text" value={q.duration || ''} onChange={e => onUpdate(idx, 'duration', e.target.value)}
                className="border rounded px-2 py-1 text-xs w-24" placeholder="Durée (ex: 5 min)" />
            )}
          </div>

          {(q.type === 'radio' || q.type === 'checkbox') && (
            <div>
              <div className="flex flex-wrap gap-1 mb-1">
                {(q.options || []).map((opt, oi) => (
                  <span key={oi} className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary-dark rounded-full text-xs">
                    {opt}
                    <button type="button" onClick={() => onUpdate(idx, 'options', q.options.filter((_, i) => i !== oi))} className="text-primary/60 hover:text-red-500">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <input type="text" className="flex-1 border rounded px-2 py-1 text-xs" placeholder="Nouvelle option..."
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = e.target.value.trim();
                      if (val) { onUpdate(idx, 'options', [...(q.options || []), val]); e.target.value = ''; }
                    }
                  }} />
                <button type="button" onClick={e => {
                  const input = e.target.previousElementSibling;
                  const val = input?.value?.trim();
                  if (val) { onUpdate(idx, 'options', [...(q.options || []), val]); input.value = ''; }
                }} className="px-2 py-1 text-xs bg-primary/10 text-primary-dark rounded hover:bg-primary/20">+</button>
              </div>
            </div>
          )}
        </div>

        <button onClick={() => onRemove(idx)} className="text-gray-300 hover:text-red-500 mt-1"><X size={14} /></button>
      </div>
    </div>
  );
};

// ─── Prévisualisation ─────────────────────────────────────────────────────────
const PreviewModal = ({ form, isOpen, onClose }) => (
  <Modal isOpen={isOpen} onClose={onClose} title={`Aperçu — ${form.name}`} size="lg">
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <ClipboardList size={16} className="text-primary" />
        <span>{form.questions.filter(q => q.type !== 'section').length} questions</span>
        {form.questions.filter(q => q.type === 'section').length > 0 && (
          <><span>·</span><span>{form.questions.filter(q => q.type === 'section').length} sections</span></>
        )}
      </div>
      <div className="space-y-3">
        {form.questions.filter(q => q.label.trim()).map(q =>
          q.type === 'section' ? (
            <div key={q.id} className="border-b-2 border-primary/20 pb-1 pt-3">
              <h4 className="text-sm font-bold text-primary uppercase tracking-wider">{q.label}</h4>
              {q.description?.trim() && <p className="text-xs text-gray-500 mt-1 italic">{q.description}</p>}
              {q.duration && <span className="text-[10px] text-primary/60 flex items-center gap-1 mt-1"><Clock size={10} /> {q.duration}</span>}
            </div>
          ) : (
            <div key={q.id} className="bg-gray-50 rounded-lg p-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {q.label} {q.required && <span className="text-red-400">*</span>}
              </label>
              {q.description?.trim() && <p className="text-xs text-gray-400 mb-2 italic">{q.description}</p>}
              {q.type === 'rating' ? (
                <div className="flex gap-1">{[1,2,3,4,5].map(n => <Star key={n} size={18} className="text-gray-300" />)}</div>
              ) : q.type === 'radio' ? (
                <div className="space-y-1">{(q.options||[]).map((o,i) => <label key={i} className="flex items-center gap-2 text-sm"><input type="radio" disabled />{o}</label>)}</div>
              ) : q.type === 'checkbox' ? (
                <div className="space-y-1">{(q.options||[]).map((o,i) => <label key={i} className="flex items-center gap-2 text-sm"><input type="checkbox" disabled />{o}</label>)}</div>
              ) : q.type === 'textarea' ? (
                <div className="w-full border rounded-lg px-3 py-2 h-16 bg-white text-gray-300 text-sm">Réponse longue...</div>
              ) : (
                <div className="w-full border rounded-lg px-3 py-2 bg-white text-gray-300 text-sm">Réponse courte...</div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  </Modal>
);

// ─── Composant principal ──────────────────────────────────────────────────────
const EmployeTemplateManagement = () => {
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [confirmDelete, setConfirmDelete] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const loadTemplates = async () => {
    try { const { data } = await getEmployeeInterviewTemplates(); setTemplates(data); } catch { toast.error('Erreur chargement templates'); }
    setLoading(false);
  };

  useEffect(() => { loadTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    const validQuestions = form.questions.filter(q => q.label.trim());
    if (!form.name.trim() || validQuestions.length === 0) { toast.error('Nom et au moins une question requis'); return; }
    try {
      const payload = { ...form, questions: validQuestions };
      if (editing) { await updateEmployeeInterviewTemplate(editing.id, payload); toast.success('Template mis à jour'); }
      else { await createEmployeeInterviewTemplate(payload); toast.success('Template créé'); }
      setShowModal(false); setEditing(null); loadTemplates();
    } catch { toast.error('Erreur sauvegarde'); }
  };

  const handleDelete = async () => {
    try { await deleteEmployeeInterviewTemplate(confirmDelete.id); toast.success('Template supprimé'); loadTemplates(); }
    catch { toast.error('Erreur suppression'); }
    setConfirmDelete(null);
  };

  const handleDuplicate = async (tpl) => {
    try {
      await createEmployeeInterviewTemplate({ name: `${tpl.name} (copie)`, questions: tpl.questions, isDefault: false });
      toast.success('Template dupliqué'); loadTemplates();
    } catch { toast.error('Erreur duplication'); }
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      name: tpl.name,
      questions: (tpl.questions || [emptyQuestion()]).map(q => ({ ...q, description: q.description || '', duration: q.duration || '' })),
      isDefault: tpl.isDefault,
    });
    setShowModal(true);
  };

  const addQuestion = () => setForm(f => ({ ...f, questions: [...f.questions, emptyQuestion()] }));
  const removeQuestion = (idx) => setForm(f => ({ ...f, questions: f.questions.filter((_, i) => i !== idx) }));
  const moveQuestion = (idx, direction) => setForm(f => {
    const qs = [...f.questions];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= qs.length) return f;
    [qs[idx], qs[newIdx]] = [qs[newIdx], qs[idx]];
    return { ...f, questions: qs };
  });
  const updateQuestion = (idx, field, value) => setForm(f => ({
    ...f, questions: f.questions.map((q, i) => i === idx ? { ...q, [field]: value } : q),
  }));
  const handleDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setForm(f => {
        const oldIdx = f.questions.findIndex(q => q.id === active.id);
        const newIdx = f.questions.findIndex(q => q.id === over.id);
        return { ...f, questions: arrayMove(f.questions, oldIdx, newIdx) };
      });
    }
  };

  const questionCount = form.questions.filter(q => q.type !== 'section' && q.label.trim()).length;
  const sectionCount = form.questions.filter(q => q.type === 'section' && q.label.trim()).length;

  if (loading) return <div className="p-4 text-gray-400">Chargement...</div>;

  return (
    <div className="px-4 sm:px-6 lg:px-8 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-700">Templates d'entretien employé</h2>
        <Btn variant="primary" size="sm" onClick={() => { setEditing(null); setForm(emptyForm()); setShowModal(true); }}>
          <Plus size={14} /> Nouveau template
        </Btn>
      </div>

      <div className="space-y-2">
        {templates.map(tpl => (
          <div key={tpl.id} className="bg-white border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <ClipboardList size={16} className="text-primary" />
                  <span className="font-medium text-gray-800">{tpl.name}</span>
                  {tpl.isDefault && <span className="text-xs bg-primary/10 text-primary-dark px-2 py-0.5 rounded-full">Par défaut</span>}
                  {tpl.structureId && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">Structure</span>}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {tpl.questions?.filter(q => q.type !== 'section').length || 0} questions
                  {tpl.questions?.filter(q => q.type === 'section').length > 0 && ` · ${tpl.questions.filter(q => q.type === 'section').length} sections`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => { setForm({ name: tpl.name, questions: tpl.questions || [], isDefault: tpl.isDefault }); setShowPreview(true); }}
                  className="p-1.5 text-gray-400 hover:text-primary" title="Aperçu"><Eye size={14} /></button>
                <button onClick={() => handleDuplicate(tpl)} className="p-1.5 text-gray-400 hover:text-accent-dark" title="Dupliquer"><Copy size={14} /></button>
                <button onClick={() => openEdit(tpl)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Modifier"><Edit size={14} /></button>
                <button onClick={() => setConfirmDelete(tpl)} className="p-1.5 text-gray-400 hover:text-red-600" title="Supprimer"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {templates.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-8">Aucun template. Créez-en un pour structurer vos entretiens employé.</p>
        )}
      </div>

      {/* Modal édition */}
      {showModal && (
        <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditing(null); }} title={editing ? 'Modifier le template' : 'Nouveau template'} size="lg" preventClose>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className={inputCls} placeholder="Ex : Bilan annuel standard" />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.isDefault} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
                Template par défaut
              </label>
              <div className="text-xs text-gray-400">
                {sectionCount > 0 && <span>{sectionCount} section{sectionCount > 1 ? 's' : ''} · </span>}
                {questionCount} question{questionCount !== 1 ? 's' : ''}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Questions <span className="text-xs text-gray-400">(glisser-déposer pour réorganiser)</span></label>
                <div className="flex gap-2">
                  <button onClick={() => setShowPreview(true)} className="text-xs text-primary hover:text-primary-dark flex items-center gap-1"><Eye size={13} /> Aperçu</button>
                  <button onClick={addQuestion} className="text-xs text-primary hover:text-primary-dark flex items-center gap-1"><Plus size={14} /> Ajouter</button>
                </div>
              </div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={form.questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {form.questions.map((q, idx) => (
                      <SortableQuestion key={q.id} q={q} idx={idx} total={form.questions.length}
                        onUpdate={updateQuestion} onRemove={removeQuestion} onMove={moveQuestion} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="secondary" size="sm" onClick={() => { setShowModal(false); setEditing(null); }}>Annuler</Btn>
              <Btn variant="primary" size="sm" onClick={handleSave} disabled={!form.name.trim()}>
                {editing ? 'Enregistrer' : 'Créer'}
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      <PreviewModal form={form} isOpen={showPreview} onClose={() => setShowPreview(false)} />

      <ConfirmModal
        open={!!confirmDelete}
        title="Supprimer le template"
        message={`Supprimer "${confirmDelete?.name}" ? Cette action est irréversible.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(null)}
        danger
      />
    </div>
  );
};

export default EmployeTemplateManagement;
